import { ReadonlyURLSearchParams } from 'next/navigation';

import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

import { StateName } from '~/components/common/give-happiness/state-sprite';
import USStatesData from '~/data/us-states.json';
import { BasicPage, BlogPost, Maybe, Page, PressRelease, Program } from '~/server/generated/contentful-graphql';
import { logger } from '~/utils/logger';

type LinkedContentModels =
  | BasicPage['__typename']
  | BlogPost['__typename']
  | Page['__typename']
  | Program['__typename']
  | PressRelease['__typename'];

export const contentfulLinkResolver = (linkType: LinkedContentModels, linkPath: string | null | undefined) => {
  switch (linkType) {
    case 'BasicPage':
    case 'Page':
    case 'Program':
      return '/' + linkPath;
    case 'BlogPost':
      return '/stories/' + linkPath;
    case 'PressRelease':
      return '/media/press-release/' + linkPath;
    default:
      console.log('unhandled contentfulLinkResolver linkType');
      return '';
  }
};

type ContentfulLinkResolver = {
  ctaLink?: Maybe<string>;
  ctaLinkContentType?: Maybe<string>;
  ctaLinkContentSlug?: Maybe<string>;
  ctaLinkAssetUrl?: Maybe<string>;
};

export function ctaLinkResolver({
  ctaLink,
  ctaLinkContentType,
  ctaLinkContentSlug,
  ctaLinkAssetUrl,
}: ContentfulLinkResolver) {
  if (!ctaLink && !ctaLinkContentSlug && !ctaLinkAssetUrl) return null;
  if (ctaLinkAssetUrl) return ctaLinkAssetUrl;
  if (ctaLink) return ctaLink;
  if (ctaLinkContentType && ctaLinkContentSlug) {
    return contentfulLinkResolver(ctaLinkContentType as LinkedContentModels, ctaLinkContentSlug);
  }
  logger.warn(`contentfulLinkResolver was unable to determine a working link`);
  return null;
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Creates a path by appending query parameters to a base pathname.
 *
 * @param {string} pathname - The path of the URL.
 * @param {URLSearchParams | ReadonlyURLSearchParams} params - The query parameters to append.
 * @returns {string} - The constructed URL with query parameters.
 */
export function createPath(pathname: string, params: URLSearchParams | ReadonlyURLSearchParams): string {
  const paramsString = params.toString();
  const queryString = `${paramsString.length ? '?' : ''}${paramsString}`;

  return `${pathname}${queryString}`;
}

export function unslug(slug: string) {
  return slug.replace(/[-_]/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

// Truncate a string nicely to a certain length
export function truncate(str: string, length: number) {
  if (str.length <= length) {
    return str;
  }
  const subString = str.substr(0, length);
  return `${subString.substr(0, subString.lastIndexOf(' '))}…`;
}

export function getStateName(abbr: string) {
  const state = USStatesData.find((s) => s.abbr === abbr);
  return state ? (state.name as StateName) : null;
}
